import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, delay, map, mergeMap, of, retry, tap } from 'rxjs';

import { VATImport } from '@/organisations/models';
import type { ManualVatFile } from '@/organisations/models';
import { PollingStatus } from '@/tax/models';

import { environment } from '@env';

const DELAY_STATUS_FETCH_MS = 1000;

const invalidErrorMessage = 'Not ready';

@Injectable({
    providedIn: 'root',
})
export class VATService {
    constructor(private http: HttpClient) {}

    createVATImportAndPollResult(
        orgId: number,
        companyId: number,
        vatImport: VATImport,
        progress: { progress: number, message?: string },
        status: { message?: string },
    ): Observable<VATImport> {
        return this.createVATImport(orgId, companyId, vatImport).pipe(
            delay(DELAY_STATUS_FETCH_MS),
            mergeMap((newVATImport) =>
                this.getVATImport(orgId, companyId, newVATImport.id).pipe(
                    tap((sync) => {
                        progress.progress = sync.progress;
                        progress.message = sync.progressMessage;
                        status.message = sync.statusMessage ?? undefined;
                        if (sync.status === PollingStatus.Init || sync.status === PollingStatus.InProgress) {
                            throw Error(invalidErrorMessage);
                        }
                    }),
                    retry({
                        delay: (error) => {
                            if (error.message !== invalidErrorMessage) {
                                throw error;
                            } else {
                                return of(true).pipe(delay(DELAY_STATUS_FETCH_MS));
                            }
                        },
                    }),
                ),
            ),
        );
    }

    pollVATImportUntilDone(orgId: number, companyId: number, vatImportId: number): Observable<VATImport> {
        return of(true).pipe(
            delay(DELAY_STATUS_FETCH_MS),
            mergeMap(() =>
                this.getVATImport(orgId, companyId, vatImportId).pipe(
                    tap((sync) => {
                        if (sync.status === PollingStatus.Init || sync.status === PollingStatus.InProgress) {
                            throw Error(invalidErrorMessage);
                        }
                    }),
                    retry({
                        delay: (error) => {
                            if (error.message !== invalidErrorMessage) {
                                throw error;
                            } else {
                                return of(true).pipe(delay(DELAY_STATUS_FETCH_MS));
                            }
                        },
                    }),
                ),
            ),
        );
    }

    pollVATImportUntilDoneWithProgress(
        orgId: number,
        companyId: number,
        vatImportId: number,
        progress: { progress: number, message?: string },
        status: { message?: string },
    ): Observable<VATImport> {
        return of(true).pipe(
            delay(DELAY_STATUS_FETCH_MS),
            mergeMap(() =>
                this.getVATImport(orgId, companyId, vatImportId).pipe(
                    tap((sync) => {
                        progress.progress = sync.progress;
                        progress.message = sync.progressMessage;
                        status.message = sync.statusMessage ?? undefined;
                        if (sync.status === PollingStatus.Init || sync.status === PollingStatus.InProgress) {
                            throw Error(invalidErrorMessage);
                        }
                    }),
                    retry({
                        delay: (error) => {
                            if (error.message !== invalidErrorMessage) {
                                throw error;
                            } else {
                                return of(true).pipe(delay(DELAY_STATUS_FETCH_MS));
                            }
                        },
                    }),
                ),
            ),
        );
    }

    getVATImport(orgId: number, companyId: number, vatImportId: number): Observable<VATImport> {
        return this.http
            .get<any>(environment.endpoints.organisationCompaniesVATImportsDetails(orgId, companyId, vatImportId))
            .pipe(map<any, VATImport>((vatImport) => new VATImport().deserialize(vatImport)));
    }

    createVATImport(orgId: number, companyId: number, vatImport: VATImport): Observable<VATImport> {
        return this.http
            .post<VATImport>(environment.endpoints.organisationCompaniesVATImportsList(orgId, companyId), vatImport.serialize())
            .pipe(map((sync) => new VATImport().deserialize(sync)));
    }

    uploadManualVATImport(orgId: number, companyId: number, vatImportId: number, formData: FormData): Observable<VATImport> {
        // The backend endpoint should accept a file upload for a specific VAT import
        return this.http
            .post<any>(`${environment.endpoints.organisationCompaniesVATImportsDetails(orgId, companyId, vatImportId)}manual-upload/`, formData)
            .pipe(map((vatImport) => new VATImport().deserialize(vatImport)));
    }

    listManualVatFiles(orgId: number, companyId: number, vatImportId: number): Observable<ManualVatFile[]> {
        return this.http
            .get<any[]>(`${environment.endpoints.organisationCompaniesVATImportsDetails(orgId, companyId, vatImportId)}manual-files/`)
            .pipe(
                map((items) =>
                    items.map((it) => ({
                        id: it.id ?? null,
                        name: it.name,
                        fileType: it.file_type ?? 'UNKNOWN',
                        uploadedAt: it.uploaded_at ?? null,
                        status: it.status_message ? 'skipped' : 'uploaded',
                        reason: it.status_message ?? undefined,
                    }) as ManualVatFile),
                ),
            );
    }

    pollManualVATImportUntilDone(
        orgId: number,
        companyId: number,
        vatImportId: number,
        progress: { progress: number; message?: string },
        status: { message?: string },
    ): Observable<VATImport> {
        return this.pollVATImportUntilDoneWithProgress(orgId, companyId, vatImportId, progress, status);
    }
}
